import axios from "axios";
import config from "@/lib/config";
import { attachApiHeaders } from "@/lib/attachApiHeaders";

const API_URL = `${config.apiUrl}/personal-data`;

const api = axios.create({
  baseURL: API_URL,
});

api.interceptors.request.use(attachApiHeaders);

/**
 * Recupera todos los datos personales del usuario autenticado.
 * @returns {Promise<object>} Los datos personales completos.
 */
export const getPersonalData = async () => {
  try {
    const res = await api.get("/");
    return res.data;
  } catch (error) {
    console.error(
      "Error fetching personal data:",
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Actualiza los datos de RUC y la contraseña asociada del usuario autenticado.
 * @param {object} rucData - Datos a actualizar. Debe incluir:
 * - {string} ruc (opcional)
 * - {string} rucPassword (opcional)
 */
export const updateRucData = async (rucData) => {
  try {
    const res = await api.put("/ruc", rucData);
    return res.data;
  } catch (error) {
    console.error(
      "Error updating RUC data:",
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Actualiza la fecha de nacimiento del usuario autenticado.
 * @param {string} birthDate - La fecha de nacimiento a actualizar (ej: "1990-01-01").
 */
export const updateBirthDate = async (birthDate) => {
  try {
    const res = await api.put("/birth-date", { birthDate });
    return res.data;
  } catch (error) {
    console.error(
      "Error updating birth date:",
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Actualiza la carga familiar para un año específico del usuario autenticado.
 * @param {object} familiarChargeData - Datos a actualizar. Debe incluir:
 * - {string} familiarChargeYear (ej: "2024")
 * - {number} familiarChargeValue (debe ser el valor del Enum 0-5)
 */
export const updateFamiliarCharge = async (familiarChargeData) => {
  try {
    const res = await api.put("/familiar-charge", familiarChargeData);
    return res.data;
  } catch (error) {
    console.error(
      "Error updating familiar charge:",
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Actualiza los datos de discapacidad y sustitución del usuario autenticado.
 * @param {object} disabilityData - Datos a actualizar. Campos opcionales:
 * - {boolean} hasDisability
 * - {number} disabilityPercentage
 * - {boolean} disabilitySubstitute
 * - {boolean} solidarityDisabilitySubstitute
 * - {string} disabledPersonId
 * - {string} disabledPersonRelation
 * - {string} substituteQualificationStartDate (ISO 8601 string)
 * - {string} disabilityCardNumber
 * - {string} disabilityCardValidityStartDate (ISO 8601 string)
 */
export const updateDisabilityData = async (disabilityData) => {
  try {
    const res = await api.put("/disability", disabilityData);
    return res.data;
  } catch (error) {
    console.error(
      "Error updating disability data:",
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Actualiza el porcentaje de seguro del usuario autenticado.
 * @param {object} insuranceData - Datos a actualizar. Debe incluir:
 * - {number} insurancePercentage
 */
export const updateInsurancePercentage = async (insuranceData) => {
  try {
    const res = await api.put("/insurance-percentage", insuranceData);
    return res.data;
  } catch (error) {
    console.error(
      "Error updating insurance percentage:",
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Actualiza el tipo de sincronización del usuario autenticado.
 * @param {object} syncData - Datos a actualizar. Debe incluir:
 * - {string} syncType (valor del Enum: MANUAL, ONCE, AUTO)
 */
export const updateSyncType = async (syncData) => {
  try {
    const res = await api.put("/sync-type", syncData);
    return res.data;
  } catch (error) {
    console.error(
      "Error updating sync type:",
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Obtiene el tipo de sincronización del usuario autenticado.
 * @returns {Promise<string>} El tipo de sincronización (valor del Enum: MANUAL, ONCE, AUTO).
 */
export const getSyncType = async () => {
  try {
    const res = await api.get("/get-sync-type");
    return res.data;
  } catch (error) {
    console.error(
      "Error fetching sync type:",
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Establece el tipo de cuenta del usuario autenticado.
 * @param {string} accountType - El tipo de cuenta a establecer ('none', 'basic', 'premium').
 * @returns {Promise<object>} Los datos personales actualizados.
 */
export const setAccountType = async (accountType) => {
  try {
    const res = await api.post("/account-type", { accountType });
    return res.data;
  } catch (error) {
    console.error(
      "Error setting account type:",
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Obtiene los datos básicos (tipo de cuenta y RUC) del usuario autenticado.
 * @returns {Promise<{ accountType: string; ruc: string }>} Objeto con tipo de cuenta y RUC.
 */
export const getBasicData = async () => {
  try {
    const res = await api.get("/basic-data");
    return res.data;
  } catch (error) {
    console.error(
      "Error fetching basic data:",
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Actualiza campos arbitrarios de los datos personales del usuario autenticado.
 * @param {object} updateQuery - Objeto con los campos a actualizar (clave-valor).
 * @returns {Promise<object>} Los datos personales actualizados.
 */
export const updatePersonalDataFields = async (updateQuery) => {
  try {
    const res = await api.put("/update-fields", updateQuery);
    return res.data;
  } catch (error) {
    console.error(
      "Error updating personal data fields:",
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Agrega los datos de facturación para el usuario autenticado.
 * Falla si billingData ya existe.
 * @param {object} billingData - Objeto con los datos de facturación:
 *   - {string} identificacion (opcional)
 *   - {string} email (opcional)
 *   - {string} direccion (opcional)
 *   - {string} nombre (opcional)
 *   - {string} telefono (opcional)
 * @returns {Promise<object>} Los datos personales actualizados.
 */
export const addBillingData = async (billingData) => {
  try {
    const res = await api.post("/billing-data", billingData);
    return res.data;
  } catch (error) {
    console.error(
      "Error adding billing data:",
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Edita los datos de facturación del usuario autenticado.
 * Solo actualiza los campos proporcionados.
 * @param {object} billingData - Objeto con los campos a actualizar:
 *   - {string} identificacion (opcional)
 *   - {string} email (opcional)
 *   - {string} direccion (opcional)
 *   - {string} nombre (opcional)
 *   - {string} telefono (opcional)
 * @returns {Promise<object>} Los datos personales actualizados.
 */
export const editBillingData = async (billingData) => {
  try {
    const res = await api.put("/billing-data", billingData);
    return res.data;
  } catch (error) {
    console.error(
      "Error editing billing data:",
      error.response?.data || error.message
    );
    throw error;
  }
};
